import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { RouteNames } from '@/router/router.types';
import Preloader from '@srm/ui/preloader';
import { buildLoginUrlWithReturnTo } from '@srm/core/routing';

interface ProtectedRouteProps {
    /** Защищённый элемент. Если не передан — рендерится <Outlet/>. */
    element?: ReactNode;
    /**
     * Разрешает изолированный маршрут по наличию access-токена без загруженного профиля.
     * Backend всё равно валидирует токен, а preview request guard блокирует обычные API.
     */
    allowAccessTokenOnly?: boolean;
}

/**
 * Route-уровневый guard. На !isAuth редиректит на /login с
 * `?returnUrl=<current>` (basename-relative).
 */
const ProtectedRoute = ({ element, allowAccessTokenOnly = false }: ProtectedRouteProps = {}) => {
    const isAuth = useTypedSelector((s) => s.auth.isAuth);
    const isLoading = useTypedSelector((s) => s.auth.isLoading);
    const hasAccessToken = useTypedSelector((s) => s.auth.hasAccessToken);
    const location = useLocation();

    if (isLoading) return <Preloader size="xl" label="Загрузка" />;

    if (!isAuth && !(allowAccessTokenOnly && hasAccessToken)) {
        const target = buildLoginUrlWithReturnTo(
            RouteNames.LOGIN,
            location.pathname,
            location.search,
            location.hash,
        );
        return <Navigate to={target} replace />;
    }

    return <>{element ?? <Outlet />}</>;
};

export default ProtectedRoute;
